import { toast } from "sonner";
import { offlineStorage, PendingRecording } from './offline-storage-service';
import { retryManager, RetryableErrorType } from './enhanced-retry-manager';
import { distributedLockManager } from './distributed-lock-manager';
import { supabase } from '@/integrations/supabase/client';
import { localLogger } from '@/lib/local-logger';
import { getMobileFileThreshold, isMobileDevice, logDeviceInfo, getDeviceInfo } from '@/lib/mobile-utils';
import { logger } from '@/utils/logger';

// Queue processing statistics
export interface QueueProcessingStats {
  totalProcessed: number;
  successful: number;
  failed: number;
  retried: number;
  permanentFailures: number;
  processingTime: number;
  averageProcessingTime: number;
  queueSize: number;
  healthScore: number;
}

// Processing priority levels
export enum ProcessingPriority {
  HIGH = 'high',     // Recent recordings, low retry count
  MEDIUM = 'medium', // Older recordings, moderate retry count
  LOW = 'low'        // Old recordings, high retry count
}

export interface ProcessingTask {
  recording: PendingRecording;
  priority: ProcessingPriority;
  estimatedProcessingTime: number;
  lastAttempt?: number;
}

export class AdaptiveQueueProcessor {
  private isProcessing = false;
  private processingStats: QueueProcessingStats = {
    totalProcessed: 0,
    successful: 0,
    failed: 0,
    retried: 0,
    permanentFailures: 0,
    processingTime: 0,
    averageProcessingTime: 0,
    queueSize: 0,
    healthScore: 1.0
  };

  private maxConcurrentProcessing = 2; // Process max 2 recordings simultaneously
  private activeProcessingTasks = new Map<string, AbortController>();
  private processingMetrics = new Map<string, { startTime: number; attempts: number }>();

  // Intelligent queue prioritization
  private prioritizeRecordings(recordings: PendingRecording[]): ProcessingTask[] {
    const now = Date.now();

    return recordings.map(recording => {
      const age = now - recording.timestamp;
      const attempts = recording.attempts || 0;
      const lastAttemptAge = recording.lastAttempt ? now - recording.lastAttempt : Infinity;

      // Calculate priority based on multiple factors
      let priority: ProcessingPriority;
      let estimatedTime: number;

      if (age < 10 * 60 * 1000 && attempts <= 1) { // Less than 10 minutes old, few attempts
        priority = ProcessingPriority.HIGH;
        estimatedTime = 30000; // 30 seconds estimated
      } else if (age < 60 * 60 * 1000 && attempts <= 3) { // Less than 1 hour old, moderate attempts
        priority = ProcessingPriority.MEDIUM;
        estimatedTime = 45000; // 45 seconds estimated
      } else {
        priority = ProcessingPriority.LOW;
        estimatedTime = 60000; // 1 minute estimated
      }

      return {
        recording,
        priority,
        estimatedProcessingTime: estimatedTime,
        lastAttempt: recording.lastAttempt
      };
    }).sort((a, b) => {
      // Sort by priority first, then by age
      if (a.priority !== b.priority) {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      return a.recording.timestamp - b.recording.timestamp; // Older first within same priority
    });
  }

  // Enhanced processing with concurrent handling
  async processQueue(userId: string): Promise<QueueProcessingStats> {
    if (this.isProcessing) {
      console.log('Queue processing already in progress');
      return this.processingStats;
    }

    this.isProcessing = true;
    const startTime = Date.now();
    const stats: QueueProcessingStats = {
      totalProcessed: 0,
      successful: 0,
      failed: 0,
      retried: 0,
      permanentFailures: 0,
      processingTime: 0,
      averageProcessingTime: 0,
      queueSize: 0,
      healthScore: 1.0
    };

    try {
      // Get ready recordings first - only proceed with health check if there's work
      let pendingRecordings = await offlineStorage.getReadyRecordings(userId);
      stats.queueSize = pendingRecordings.length;

      if (pendingRecordings.length === 0) {
        console.log('No recordings ready for processing');
        return stats;
      }

      // ⚠️ CRITICAL: Filter out recordings already locked by other tabs
      // This prevents retry storms when multiple tabs try to process the same recording
      console.log(`🔍 Checking ${pendingRecordings.length} recording(s) for active locks...`);

      const filterResult = await this.filterLockedRecordings(pendingRecordings);
      pendingRecordings = filterResult.unlocked;

      if (filterResult.locked.length > 0) {
        console.log(`🔒 Skipping ${filterResult.locked.length} recording(s) already being processed by another tab`);
        stats.retried += filterResult.locked.length;
        toast.info(
          `${filterResult.locked.length} recording${filterResult.locked.length > 1 ? 's' : ''} already being processed on another tab`,
          { duration: 3000 }
        );
      }

      if (pendingRecordings.length === 0) {
        console.log('No unlocked recordings ready for processing');
        return stats;
      }

      console.log('🚀 Starting adaptive queue processing...');

      // OPTIMIZATION: Only perform health check if there are pending recordings to process
      // This avoids unnecessary edge function calls on every page load
      const systemHealth = await retryManager.performHealthCheck();

      if (!systemHealth.isHealthy) {
        console.warn('⚠️ System not healthy, deferring queue processing');

        if (systemHealth.circuitBreakerOpen) {
          toast.warning('Service temporarily unavailable - queue processing paused');
        } else {
          toast.info('Connection issues detected - will retry when service recovers');
        }

        return stats;
      }

      if (systemHealth.transcriptionServiceStatus === 'degraded') {
        toast.info('Transcription service experiencing high load - processing may be slower');
        this.maxConcurrentProcessing = 1; // Reduce concurrent processing
      } else {
        this.maxConcurrentProcessing = 2; // Normal concurrent processing
      }

      // Prioritize recordings
      const tasks = this.prioritizeRecordings(pendingRecordings);

      console.log(`Processing ${tasks.length} recordings with priorities:`, {
        high: tasks.filter(t => t.priority === ProcessingPriority.HIGH).length,
        medium: tasks.filter(t => t.priority === ProcessingPriority.MEDIUM).length,
        low: tasks.filter(t => t.priority === ProcessingPriority.LOW).length
      });

      toast.info(`Processing ${tasks.length} offline recording${tasks.length > 1 ? 's' : ''}...`);

      console.log(`📋 Found ${pendingRecordings.length} recording${pendingRecordings.length > 1 ? 's' : ''} to process`);

      // Process recordings ONE AT A TIME for better reliability and progress feedback
      console.log('🔄 Processing recordings sequentially (one at a time)...');

      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        const recordingNum = i + 1;
        const totalRecordings = tasks.length;

        console.log(`\n📝 Processing recording ${recordingNum}/${totalRecordings}: ${task.recording.title}`);
        toast.loading(`Processing recording ${recordingNum} of ${totalRecordings}...`, {
          id: 'queue-processing',
          description: task.recording.title
        });

        await this.processRecordingTask(task, stats);

        // Small delay between recordings to prevent overwhelming the system
        if (i < tasks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      toast.dismiss('queue-processing');

      // Calculate final statistics
      stats.processingTime = Date.now() - startTime;
      stats.averageProcessingTime = stats.totalProcessed > 0 ?
        stats.processingTime / stats.totalProcessed : 0;

      // Calculate health score based on success rate
      stats.healthScore = stats.totalProcessed > 0 ?
        stats.successful / stats.totalProcessed : 1.0;

      // Update global statistics
      this.processingStats = stats;

      // User feedback
      if (stats.successful > 0) {
        toast.success(`Successfully processed ${stats.successful} recording${stats.successful > 1 ? 's' : ''}`);
      }

      if (stats.failed > 0) {
        toast.warning(`${stats.failed} recording${stats.failed > 1 ? 's' : ''} will retry later`);
      }

      if (stats.permanentFailures > 0) {
        toast.error(`${stats.permanentFailures} recording${stats.permanentFailures > 1 ? 's' : ''} failed permanently`);
      }

      console.log('✅ Queue processing completed:', stats);
      return stats;

    } catch (error) {
      console.error('❌ Queue processing failed:', error);

      localLogger.setContext('queue_processing', {
        userId,
        stats,
        systemHealth: retryManager.getCurrentHealth()
      });

      localLogger.captureError(error as Error, {
        component: 'adaptive_queue_processor',
        operation: 'queue_processing'
      });

      toast.error('Failed to process offline recordings');
      return stats;

    } finally {
      this.isProcessing = false;

      // Clean up any remaining active tasks
      for (const [taskId, controller] of this.activeProcessingTasks) {
        controller.abort();
        this.activeProcessingTasks.delete(taskId);
      }
    }
  }

  // Create processing batches for concurrent execution
  private createProcessingBatches(tasks: ProcessingTask[]): ProcessingTask[][] {
    const batches: ProcessingTask[][] = [];

    for (let i = 0; i < tasks.length; i += this.maxConcurrentProcessing) {
      batches.push(tasks.slice(i, i + this.maxConcurrentProcessing));
    }

    return batches;
  }

  // Process a batch of recordings concurrently
  private async processBatch(batch: ProcessingTask[], stats: QueueProcessingStats): Promise<void> {
    console.log(`Processing batch of ${batch.length} recordings`);

    const promises = batch.map(task => this.processRecordingTask(task, stats));
    await Promise.allSettled(promises);
  }

  // Minimum recording duration in milliseconds - recordings shorter than this are auto-discarded
  private static readonly MIN_RECORDING_DURATION_MS = 3000; // 3 seconds

  // Process individual recording with enhanced error handling
  private async processRecordingTask(task: ProcessingTask, stats: QueueProcessingStats): Promise<void> {
    const { recording } = task;
    const startTime = Date.now();
    let lockAcquired = false;

    // Auto-discard recordings that are too short to produce meaningful transcriptions
    if (recording.duration > 0 && recording.duration < AdaptiveQueueProcessor.MIN_RECORDING_DURATION_MS) {
      const durationSec = (recording.duration / 1000).toFixed(1);
      console.log(`🗑️ Auto-discarding recording ${recording.id}: too short (${durationSec}s). Minimum is ${AdaptiveQueueProcessor.MIN_RECORDING_DURATION_MS / 1000}s.`);
      await offlineStorage.removeRecording(recording.id);
      stats.totalProcessed++;
      stats.successful++; // Count as resolved (not failed) since we intentionally discarded it
      toast.info(`Discarded short recording (${durationSec}s)`, {
        description: 'Recordings under 3 seconds are too short to transcribe.'
      });
      return;
    }

    try {
      // ⚠️ CRITICAL: Acquire distributed lock to prevent multi-tab duplicate processing
      // This ensures only ONE browser tab/window processes this recording
      lockAcquired = await distributedLockManager.acquireLock(recording.id, recording.user_id);

      if (!lockAcquired) {
        // Lock already held by another tab/window
        console.log(`🔒 Recording ${recording.id} already being processed by another tab - skipping`);
        toast.info(`Recording "${recording.title}" is being processed on another tab`, {
          description: 'Try again after it completes'
        });
        stats.retried++;
        return;
      }

      console.log(`🔓 Acquired processing lock for recording ${recording.id}`);

      // Create abort controller for this task
      const controller = new AbortController();
      this.activeProcessingTasks.set(recording.id, controller);

      // Track processing metrics
      this.processingMetrics.set(recording.id, {
        startTime,
        attempts: recording.attempts || 0
      });

      console.log(`🔄 Processing recording: ${recording.title} (Priority: ${task.priority})`);

      // Update status to uploading
      await offlineStorage.updateRecordingStatus(recording.id, 'uploading');

      // Attempt transcription
      const result = await this.transcribeRecording(recording, controller.signal);

      stats.totalProcessed++;

      if (result.success) {
        // FIX 1: Skip saveTranscriptionToDatabase() for server-side queued transcriptions
        // When uploadToQueueForProcessing() returns, transcription is still in-flight on server.
        // The server (process-queue) will create the meeting once transcription completes.
        // Don't create it here with empty transcript → "No Speech Detected"
        if (!result.queuedForServerProcessing) {
          // Save to database (only for client-side completed transcriptions)
          await this.saveTranscriptionToDatabase(recording, result.data, result.participants || []);
        }

        await offlineStorage.removeRecording(recording.id);

        stats.successful++;
        console.log(`✅ Successfully processed recording: ${recording.title}`);

      } else {
        // Check if this is just a "processing" status (not a real error)
        const isStillProcessing = result.error?.isProcessing === true;

        if (isStillProcessing) {
          // Transcription is still in progress - schedule a poll retry with shorter delay
          console.log(`Transcription still processing for ${recording.id}, will poll again in 10 seconds`);

          // Update next attempt time without incrementing attempts counter
          await offlineStorage.updateRecordingRetry(
            recording.id,
            'pending',
            recording.attempts || 0, // Don't increment attempts for processing status
            Date.now() + 10000 // Poll again in 10 seconds
          );

          stats.retried++;
          // Don't count as failed since it's still processing
        } else {
          // Handle real failure with smart retry logic
          const currentAttempts = (recording.attempts || 0) + 1;
          const retryScheduled = await retryManager.scheduleRetry(recording, result.error, currentAttempts);

          if (retryScheduled) {
            stats.retried++;
          } else {
            stats.permanentFailures++;
          }

          stats.failed++;
        }
      }

    } catch (error) {
      console.error(`❌ Failed to process recording ${recording.id}:`, error);

      // Check if this is just a "processing" status (not a real error)
      const isStillProcessing = (error as any)?.isProcessing === true;

      if (isStillProcessing) {
        // Transcription is still in progress - schedule a poll retry with shorter delay
        console.log(`Transcription still processing for ${recording.id}, will poll again in 10 seconds`);

        await offlineStorage.updateRecordingRetry(
          recording.id,
          'pending',
          recording.attempts || 0, // Don't increment attempts for processing status
          Date.now() + 10000 // Poll again in 10 seconds
        );

        stats.retried++;
        // Don't count as failed or increment totalProcessed since it's still processing
      } else {
        // Enhanced error handling for real errors
        const currentAttempts = (recording.attempts || 0) + 1;
        const retryScheduled = await retryManager.scheduleRetry(recording, error, currentAttempts);

        if (!retryScheduled) {
          stats.permanentFailures++;
        } else {
          stats.retried++;
        }

        stats.failed++;
        stats.totalProcessed++;
      }

    } finally {
      // Safety net: if any path above left the recording in 'uploading' (e.g. a throw
      // inside the retry manager itself), it would be invisible to the queue — which
      // only reads 'pending' — until the 10-minute stale sweep. Reset it here instead.
      if (lockAcquired) {
        try {
          const current = await offlineStorage.getRecording(recording.id);
          if (current?.status === 'uploading') {
            console.log(
              `🔄 [RECOVERY] Recording ${recording.id} left in 'uploading' after processing - resetting to 'pending'`
            );
            await offlineStorage.updateRecordingStatus(recording.id, 'pending');
          }
        } catch (cleanupErr) {
          console.error(`Failed to verify recording status for ${recording.id}:`, cleanupErr);
        }
      }

      // Clean up
      this.activeProcessingTasks.delete(recording.id);
      this.processingMetrics.delete(recording.id);

      // ⚠️ CRITICAL: Release the distributed lock to allow other tabs to process
      if (lockAcquired) {
        await distributedLockManager.releaseLock(recording.id, recording.user_id);
      }

      const processingTime = Date.now() - startTime;
      console.log(`Processing completed for ${recording.id} in ${processingTime}ms`);
    }
  }

  // Enhanced transcription with timeout and abort signal
  private async transcribeRecording(
    recording: PendingRecording,
    abortSignal: AbortSignal
  ): Promise<{ success: boolean; data?: any; error?: any; participants?: string[]; queuedForServerProcessing?: boolean }> {
    try {
      // CRITICAL: Validate blob before processing (iOS Safari can lose blob references)
      const hasAudio = recording.audioBlob && recording.audioBlob.size > 0;
      const hasDuration = recording.duration && recording.duration > 3000; // At least 3 seconds

      if (!hasAudio || !hasDuration) {
        const errorReason = !hasAudio
          ? 'Recording contains no audio data'
          : 'Recording is too short (less than 3 seconds)';

        console.error(`❌ ${errorReason} for recording ${recording.id}`);

        // Mark as permanent failure with helpful error message
        await offlineStorage.updateRecordingStatus(recording.id, 'error');

        const actionMessage = !hasAudio
          ? 'The recording appears to have no audio. Please try recording again in a quiet environment.'
          : 'Recordings must be at least 3 seconds long.';

        toast.error(errorReason, {
          description: actionMessage,
          duration: 8000
        });

        return {
          success: false,
          error: new Error(errorReason)
        };
      }

      // Log device information for diagnostics
      const deviceInfo = logDeviceInfo();

      // AUTOMATIC LARGE FILE DETECTION
      // Above the threshold the audio is uploaded as raw binary via upload-to-assemblyai
      // instead of being inlined as base64 JSON into the transcribe request (see
      // getMobileFileThreshold for why the inline path cannot take larger bodies).
      const LARGE_FILE_DURATION_MS = 15 * 60 * 1000; // 15 minutes
      const LARGE_FILE_SIZE_BYTES = getMobileFileThreshold(); // 10MB (mobile and desktop)
      const audioSize = recording.audioSize || recording.audioBlob.size;
      const isLargeFile = recording.duration > LARGE_FILE_DURATION_MS || audioSize > LARGE_FILE_SIZE_BYTES;

      console.log(`📊 File Analysis:`, {
        deviceType: deviceInfo.isMobile ? 'Mobile' : 'Desktop',
        fileSize: `${Math.round(audioSize / 1024 / 1024)}MB`,
        duration: `${Math.round(recording.duration / 1000)}s`,
        threshold: `${Math.round(LARGE_FILE_SIZE_BYTES / 1024 / 1024)}MB`,
        isLargeFile,
        estimatedMemory: `${Math.round(deviceInfo.estimatedMemory / 1024 / 1024)}MB`,
        blobValid: true
      });

      if (isLargeFile && !recording.transcriptId) {
        console.log(`🔍 Large file detected (Duration: ${Math.round(recording.duration / 1000)}s, Size: ${Math.round(audioSize / 1024 / 1024)}MB)`);
        console.log(`📤 Using queue-based upload to prevent ${deviceInfo.isMobile ? 'mobile memory issues' : 'Edge Function payload limit'}...`);

        // Use queue-based upload for large files
        return await this.uploadToQueueForProcessing(recording);
      }

      // Check if we're resuming an existing transcription
      if (recording.transcriptId) {
        console.log(`Resuming transcription for ${recording.id} with transcript ID: ${recording.transcriptId}`);

        // Verify user session is still valid before invoking edge function
        const { data: { session: pollSession }, error: pollSessionError } = await supabase.auth.getSession();
        if (pollSessionError || !pollSession) {
          console.error('❌ Authentication error during poll: User session is not available', pollSessionError);
          throw new Error('User session expired or not available. Please sign in again.');
        }

        const response = await supabase.functions.invoke('transcribe', {
          body: { transcriptId: recording.transcriptId, action: 'poll' }
        });

        if (response.error) {
          // Edge function error while polling — clear the stale transcriptId if
          // audio is available so the next attempt starts a fresh transcription.
          if (recording.audioBlob && recording.audioBlob.size > 0) {
            console.log(`[RECOVERY] Poll edge-function error for ${recording.id} — clearing stale transcriptId to retry fresh`);
            await offlineStorage.updateRecordingWithTranscriptId(recording.id, null, 'pending', recording.attempts || 0);
          }
          throw new Error(`Resume failed: ${await this.extractEdgeFunctionError(response.error)}`);
        }

        if (response.data?.status === 'completed') {
          return { success: true, data: response.data };
        } else if (response.data?.status === 'processing' || response.data?.status === 'queued') {
          // Still processing - this is NOT an error, just needs more time
          console.log(`Transcription still processing for ${recording.id}, will poll again later`);
          await offlineStorage.updateRecordingWithTranscriptId(
            recording.id,
            response.data.transcriptId || recording.transcriptId,
            'pending'
          );
          // Return a special status that indicates we should wait without counting as a failure
          return { success: false, error: { message: 'Transcription still processing', isProcessing: true } };
        } else if (response.data?.status === 'timeout' || response.data?.retryable) {
          // Update transcript ID and schedule retry
          await offlineStorage.updateRecordingWithTranscriptId(
            recording.id,
            response.data.transcriptId || recording.transcriptId,
            'pending'
          );
          return { success: false, error: new Error('Transcription timeout - will resume later') };
        } else if (response.data?.status === 'error') {
          // AssemblyAI job failed. If audio is still available, clear the stale
          // transcriptId so the next retry re-submits a fresh job instead of
          // re-polling the dead one.
          if (recording.audioBlob && recording.audioBlob.size > 0) {
            console.log(`[RECOVERY] AssemblyAI error for ${recording.id} — clearing stale transcriptId to retry from scratch`);
            await offlineStorage.updateRecordingWithTranscriptId(recording.id, null, 'pending', recording.attempts || 0);
          }
          throw new Error(`Transcription failed: ${response.data.error || 'Unknown error'}`);
        } else {
          // Unexpected status (e.g. expired job). Clear the stale transcriptId so
          // the next retry re-submits a fresh transcription instead of looping.
          const unexpectedStatus = response.data?.status ?? 'unknown';
          console.log(`[RECOVERY] Unexpected poll status "${unexpectedStatus}" for ${recording.id} — clearing stale transcriptId`);
          if (recording.audioBlob && recording.audioBlob.size > 0) {
            await offlineStorage.updateRecordingWithTranscriptId(recording.id, null, 'pending', recording.attempts || 0);
          } else {
            // No audio left — can't re-transcribe, mark as permanent failure
            await offlineStorage.updateRecordingStatus(recording.id, 'error');
            return { success: false, error: new Error('Audio no longer available and transcription job expired') };
          }
          throw new Error(`Unexpected poll status: ${unexpectedStatus}`);
        }
      }

      // Start new transcription (for normal-sized files)
      console.log(`🔄 Converting audio to base64 (${Math.round(audioSize / 1024 / 1024)}MB)...`);
      const base64Audio = await this.blobToBase64(recording.audioBlob);
      console.log(`✅ Base64 conversion complete`);

      // Extract audio format information from the blob
      const audioMimeType = recording.audioBlob.type || 'audio/webm';
      console.log('Sending audio to transcription service. MIME type:', audioMimeType, 'Size:', recording.audioBlob.size, 'bytes');

      // Check abort signal before making the request
      if (abortSignal.aborted) {
        throw new Error('Processing aborted');
      }

      // Verify user session is still valid before invoking edge function
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        console.error('❌ Authentication error: User session is not available', sessionError);
        throw new Error('User session expired or not available. Please sign in again.');
      }

      if (!session.access_token) {
        console.error('❌ Authentication error: No access token in session');
        throw new Error('User session has no access token. Please sign in again.');
      }

      // Fetch participants for better speaker identification
      const participants: string[] = [];

      try {
        // 1. Fetch current user's name
        const { data: userData } = await supabase
          .from('users')
          .select('name')
          .eq('id', recording.user_id)
          .maybeSingle();

        if (userData?.name) {
          participants.push(userData.name);
        }

        // 2. Fetch meeting participants if possible
        const targetMeetingId = recording.meetingId || recording.id;
        if (targetMeetingId) {
          const { data: pData } = await supabase
            .from('meeting_participants')
            .select('name')
            .eq('meeting_id', targetMeetingId);

          if (pData) {
            pData.forEach(p => {
              if (p.name && !participants.includes(p.name)) {
                participants.push(p.name);
              }
            });
          }
        }
      } catch (pError) {
        console.error('Error fetching participants for speaker ID:', pError);
      }

      console.log(`📤 Invoking transcribe function with valid session token...`);
      const response = await supabase.functions.invoke('transcribe', {
        body: {
          audioData: base64Audio,
          mimeType: audioMimeType,  // Pass format information to edge function
          config: {
            speech_models: [recording.transcription_model || 'universal-3-pro'],  // New API: array of speech models
            participants: participants // Pass participants for speaker identification
          }
        }
      });

      if (!response) {
        console.error('❌ Edge function returned null response');
        throw new Error('Edge function returned null response - session may have expired');
      }


      if (response.error) {
        // [RECOVERY] "Failed to send a request" is a FunctionsFetchError: the browser's
        // fetch rejected before any HTTP response arrived. With a valid session and a
        // reachable network, that means the ~33%-inflated base64 JSON body was rejected
        // by the gateway/worker before our function ran (no worker logs at all).
        // Re-sending the identical payload cannot succeed, so escalate to the binary
        // upload path instead of burning the remaining retry budget.
        const isFetchFailure = response.error.message?.includes('Failed to send a request');
        if (isFetchFailure && recording.audioBlob && recording.audioBlob.size > 0) {
          console.log(
            `[RECOVERY] Inline transcribe request never reached the edge function for ${recording.id} ` +
            `(${Math.round(audioSize / 1024 / 1024)}MB) - falling back to direct AssemblyAI upload`
          );
          return await this.uploadToQueueForProcessing(recording);
        }

        // For non-2xx responses supabase-js only gives us the generic
        // "Edge Function returned a non-2xx status code"; the real reason is in the
        // JSON body on error.context. Surface it so the retry manager can classify it
        // (e.g. "no text was generated" must not be retried ten times).
        const detailedMessage = await this.extractEdgeFunctionError(response.error);

        // Check if this is a "no audio" scenario from edge function
        const errorMsg = detailedMessage.toLowerCase();
        const isNoAudioError = errorMsg.includes('empty') ||
                              errorMsg.includes('no audio') ||
                              errorMsg.includes('no speech') ||
                              errorMsg.includes('no detectable speech') ||
                              errorMsg.includes('no text was generated') ||
                              errorMsg.includes('silent');

        const fullError = isNoAudioError
          ? `Recording contains no audio: ${detailedMessage}`
          : `Transcription failed: ${detailedMessage}`;

        throw new Error(fullError);
      }

      if (response.data?.status === 'completed') {
        return {
          success: true,
          data: response.data,
          participants
        };
      } else if (response.data?.status === 'timeout' && response.data.transcriptId) {
        // Store transcript ID for resuming
        await offlineStorage.updateRecordingWithTranscriptId(
          recording.id,
          response.data.transcriptId,
          'pending'
        );
        return { success: false, error: new Error('Transcription timeout - will resume later') };
      } else if (response.data?.status === 'processing' && response.data.transcriptId) {
        // Transcription is still processing - store transcript ID for polling on next retry
        console.log(`Transcription still processing for ${recording.id}, storing transcript ID: ${response.data.transcriptId}`);
        await offlineStorage.updateRecordingWithTranscriptId(
          recording.id,
          response.data.transcriptId,
          'pending'
        );
        return { success: false, error: new Error('Transcription still processing - will poll later') };
      } else {
        throw new Error('Unexpected transcription response');
      }

    } catch (error) {
      console.error('❌ Transcription error:', {
        recordingId: recording.id,
        error: error instanceof Error ? error.message : String(error),
        hasTranscriptId: !!recording.transcriptId,
        attempts: recording.attempts || 0,
        deviceType: isMobileDevice() ? 'mobile' : 'desktop'
      });

      // Add device context to local log
      localLogger.setContext('device_info', getDeviceInfo());

      return { success: false, error };
    }
  }

  // Pull the real error message out of a supabase-js FunctionsHttpError.
  // For non-2xx responses `error.message` is always the generic
  // "Edge Function returned a non-2xx status code"; the edge function's actual
  // `{ error, details }` JSON lives on `error.context` (a Response).
  private async extractEdgeFunctionError(error: any): Promise<string> {
    const fallback: string = error?.message || 'Unknown edge function error';
    const ctx = error?.context;
    if (!ctx || typeof ctx.json !== 'function') {
      return fallback;
    }

    try {
      const payload = await ctx.json();
      const primary = payload?.error || payload?.message;
      if (!primary) return fallback;
      return payload?.details ? `${primary}: ${payload.details}` : String(primary);
    } catch {
      // Body already consumed or not JSON - keep the generic message
      return fallback;
    }
  }

  // New method: Upload large files directly to AssemblyAI (bypasses Edge Function memory limits)
  // queuedForServerProcessing tells the caller the server will create the meeting once
  // transcription finishes, so it must not save an empty transcript itself.
  private async uploadToQueueForProcessing(recording: PendingRecording): Promise<{ success: boolean; data?: any; error?: any; queuedForServerProcessing?: boolean }> {
    try {
      console.log(`📤 Uploading large recording directly to AssemblyAI...`);

      // Verify user session is still valid before invoking edge functions
      const { data: { session: uploadSession }, error: uploadSessionError } = await supabase.auth.getSession();
      if (uploadSessionError || !uploadSession) {
        console.error('❌ Authentication error during upload: User session is not available', uploadSessionError);
        throw new Error('User session expired or not available. Please sign in again.');
      }

      // Notify user about automatic queue upload
      const durationMinutes = Math.round(recording.duration / 1000 / 60);
      const sizeMB = Math.round((recording.audioSize || recording.audioBlob.size) / 1024 / 1024);

      toast.info(`Large recording detected (${durationMinutes}min, ${sizeMB}MB)`, {
        description: 'Uploading directly to AssemblyAI...',
        duration: 5000
      });

      // 1. Upload directly to AssemblyAI via Edge Function
      console.log('Uploading to AssemblyAI via Edge Function...');

      // Convert Blob to ArrayBuffer using FileReader (more reliable on iOS than blob.arrayBuffer())
      const audioArrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result instanceof ArrayBuffer) {
            console.log(`✅ Converted to ArrayBuffer (${Math.round(reader.result.byteLength / 1024 / 1024)}MB)`);
            resolve(reader.result);
          } else {
            reject(new Error('FileReader did not return ArrayBuffer'));
          }
        };
        reader.onerror = () => reject(new Error('FileReader error'));
        reader.readAsArrayBuffer(recording.audioBlob);
      });

      const { data: uploadData, error: uploadError } = await supabase.functions.invoke('upload-to-assemblyai', {
        body: audioArrayBuffer,
        headers: {
          'Content-Type': recording.audioBlob.type || 'audio/webm'
        }
      });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      if (!uploadData || !uploadData.upload_url) {
        throw new Error('No upload URL returned from server');
      }

      console.log('✅ AssemblyAI upload successful');

      // 2. Create pending_recordings entry with the AssemblyAI upload URL
      console.log('Creating database entry...');
      const { data: recordData, error: recordError } = await supabase
        .from('pending_recordings')
        .insert({
          id: crypto.randomUUID(),
          meeting_id: recording.id,
          user_id: recording.user_id,
          audio_data: uploadData.upload_url, // Store AssemblyAI URL in audio_data field
          status: 'pending',
          template: recording.template, // CRITICAL: Pass the template to prevent fallback selection for large files
          transcription_model: recording.transcription_model || 'universal'  // Pass user's model preference
        } as any)
        .select('id')
        .single();

      if (recordError) {
        console.error('Database insert error:', recordError);
        throw new Error(`Database insert failed: ${recordError.message}`);
      }

      console.log('✅ Database entry created:', recordData.id);

      // 3. Trigger queue processor
      console.log('Triggering queue processor...');
      const processResponse = await supabase.functions.invoke('process-queue', {
        method: 'POST'
      });

      if (processResponse.error) {
        console.warn('Queue trigger warning:', processResponse.error);
      }

      // Remove the recording from IndexedDB since it's now on the server
      await offlineStorage.removeRecording(recording.id);

      // FIX: Create skeleton meeting in database immediately for large files
      // This allows the client to navigate to the meeting detail page and show a loading state
      // The server-side process-queue will update this skeleton with actual transcript and AI insights
      console.log('📝 Creating skeleton meeting for large file processing...');
      const templateValue = recording.template || 'general';
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const timeStr = now.toTimeString().split(' ')[0]; // HH:MM:SS

      const { error: skeletonError } = await supabase
        .from('meetings')
        .insert({
          id: recording.id,
          user_id: recording.user_id,
          title: 'Recording (transcribing...)',
          template: templateValue,
          transcription: null, // Null until server processes it
          duration_minutes: Math.max(1, durationMinutes),
          transcription_status: 'processing', // Mark as processing
          date: dateStr,
          meeting_date: dateStr,
          time: timeStr,
          meeting_time: timeStr
        })
        .select('id')
        .single();

      if (skeletonError) {
        console.warn('⚠️ Warning: Could not create skeleton meeting (may already exist):', skeletonError.message);
        // Don't throw - the meeting might already exist or the error might be non-critical
      } else {
        console.log('✅ Skeleton meeting created:', recording.id);
      }

      // Notify user of successful upload
      toast.success('Large recording uploaded successfully', {
        description: 'Your recording will be transcribed on the server. Check back in a few minutes.',
        duration: 5000
      });

      // Return success with queuedForServerProcessing marker to prevent
      // premature saveTranscriptionToDatabase() call with empty transcript
      return {
        success: true,
        queuedForServerProcessing: true,  // FIX 1: Signal that server will handle meeting creation
        data: {
          message: 'Large file uploaded to server queue for processing',
          uploadUrl: uploadData.upload_url,
          pendingRecordingId: recordData.id
        }
      };

    } catch (error) {
      console.error(`❌ Failed to upload via AssemblyAI:`, error);
      toast.error('Failed to upload large recording', {
        description: error instanceof Error ? error.message : 'Unknown error',
        duration: 5000
      });
      return { success: false, error };
    }
  }

  // Enhanced database saving with validation
  private async saveTranscriptionToDatabase(recording: PendingRecording, transcriptionData: any, participants: string[] = []): Promise<void> {
    try {
      let finalTranscript = transcriptionData.transcript;
      let finalTitle = this.generateFallbackTitle();

      // FIX 2: Defensive guard - check if this meeting is still being processed server-side
      // If pending_recordings row exists, transcription is still in-flight - DON'T save as "No Speech Detected"
      if (!finalTranscript || finalTranscript.trim().length === 0) {
        console.warn(`Empty transcript for recording ${recording.id}, checking for server-side processing...`);

        // Check if there's a pending_recordings entry for this meeting
        const { data: pending } = await supabase
          .from('pending_recordings')
          .select('id')
          .eq('meeting_id', recording.id)
          .maybeSingle();

        if (pending) {
          console.log(`FIX 2: Pending transcription still in-flight server-side. Aborting saveTranscriptionToDatabase() to prevent "No Speech Detected" creation.`);
          throw new Error('Meeting transcription is still in-flight on server. Do not save with empty transcript.');
        }

        console.warn(`No pending_recordings found, attempting automatic recovery...`);

        // AUTOMATIC RETRY: Try to recover from AssemblyAI before giving up
        if (transcriptionData.transcriptId) {
          try {
            console.log(`🔄 Attempting automatic recovery for transcript ID: ${transcriptionData.transcriptId}`);

            const { data: retryResult, error: retryError } = await supabase.functions.invoke('transcribe', {
              body: {
                action: 'poll',
                transcriptId: transcriptionData.transcriptId,
                requestId: `auto_retry_${recording.id}_${Date.now()}`
              }
            });

            if (!retryError && retryResult?.transcript && retryResult.transcript.trim().length > 0) {
              console.log(`✅ Automatic recovery succeeded! Retrieved ${retryResult.transcript.length} chars`);
              finalTranscript = retryResult.transcript;
              transcriptionData.transcript = retryResult.transcript;
              transcriptionData.audioDuration = retryResult.audioDuration;
              transcriptionData.confidence = retryResult.confidence;

              toast.success('Transcript recovered successfully!', {
                description: `Retrieved ${Math.round(retryResult.transcript.length / 100) / 10}K characters from transcription.`,
                duration: 5000
              });
            } else {
              console.warn(`❌ Automatic recovery failed or returned empty transcript`);
              throw new Error('Recovery returned empty transcript');
            }
          } catch (retryError) {
            console.error(`❌ Automatic recovery attempt failed:`, retryError);
            // Fall through to "No Speech Detected" handling below
            finalTranscript = '(No speech detected in recording)';
            finalTitle = 'No Speech Detected';

            toast.warning('No speech detected in recording', {
              description: 'The recording was processed but no speech was found.',
              duration: 5000
            });
          }
        } else {
          // No transcript ID available, can't retry
          console.warn(`No transcript ID available for recovery`);
          finalTranscript = '(No speech detected in recording)';
          finalTitle = 'No Speech Detected';

          toast.warning('No speech detected in recording', {
            description: 'The recording was processed but no speech was found.',
            duration: 5000
          });
        }
      }

      // Generate meeting data with temporary title
      const meetingDate = new Date(recording.timestamp);

      // Get valid template ID from database (required by trigger validation)
      let templateValue = recording.template;

      // If no template or invalid template, fetch a valid one from database
      if (!templateValue || templateValue.trim() === '' || templateValue === 'none' || templateValue === 'general') {
        console.log('No specific template provided or generic requested, checking user defaults...');

        try {
          // 1. Try to get user's default template preference
          const { data: settings } = await supabase
            .from('user_settings')
            .select('default_template')
            .eq('user_id', recording.user_id)
            .maybeSingle();

          if (settings?.default_template) {
            console.log(`Using user's default template: ${settings.default_template}`);
            templateValue = settings.default_template;
          } else {
            // 2. Fall back to 'general' template if it exists
            const { data: generalTemplate } = await supabase
              .from('templates')
              .select('id')
              .eq('name', 'general')
              .maybeSingle();

            if (generalTemplate) {
              templateValue = generalTemplate.id;
            } else {
              // 3. Absolute fallback: use first available meeting template (sorted by name for consistency)
              const { data: firstTemplate } = await supabase
                .from('templates')
                .select('id')
                .eq('template_type', 'meeting')
                .order('name')
                .limit(1)
                .maybeSingle();

              if (firstTemplate) {
                templateValue = firstTemplate.id;
              } else {
                throw new Error('No meeting templates available in database');
              }
            }
          }

          console.log(`Resolved fallback template: ${templateValue}`);
        } catch (templateError) {
          console.error('Failed to fetch fallback template:', templateError);
          // Don't throw here, we'll try to proceed or handle it later
        }
      }

      console.log(`📝 Creating meeting with template: ${templateValue}`, {
        recordingId: recording.id,
        hasTemplate: !!recording.template,
        templateValue
      });

      // Calculate duration in minutes, ensuring at least 1 minute for database constraint
      const durationMinutes = Math.max(1, Math.round(recording.duration / 1000 / 60));

      // Use actual audio duration from AssemblyAI if available, otherwise fall back to recording duration
      const actualAudioDuration = transcriptionData.audioDuration || Math.round(recording.duration / 1000);

      const meetingData = {
        id: recording.id, // Add required id field
        user_id: recording.user_id,
        title: finalTitle, // Use generated title (fallback or "No Speech Detected")
        template: templateValue, // Use validated template value
        transcription: finalTranscript, // Use processed transcript
        duration_minutes: durationMinutes, // Ensure at least 1 minute
        audio_duration: actualAudioDuration, // Audio duration in seconds from AssemblyAI
        started_at: meetingDate.toISOString(),
        ended_at: new Date(recording.timestamp + recording.duration).toISOString(),
        date: meetingDate.toLocaleDateString('en-US'),
        time: meetingDate.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        }),
        meeting_date: meetingDate.toISOString().split('T')[0],
        meeting_time: meetingDate.toTimeString().split(' ')[0],
        // Use null status to allow generate-all-insights lock acquisition to succeed
        // The generate-all-insights function will update this to 'processing' when it claims the lock,
        // then to 'completed' once AI insights are generated
        transcription_status: null
      };

      const { error } = await supabase.from('meetings').upsert(meetingData);

      if (error) {
        console.error('❌ Failed to upsert meeting:', {
          error: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          meetingId: recording.id
        });
        throw new Error(`Database save failed: ${error.message}`);
      }

      console.log(`✅ Successfully saved meeting to database:`, {
        meetingId: recording.id,
        title: finalTitle,
        transcriptLength: finalTranscript.length,
        template: templateValue,
        duration: durationMinutes
      });

      // Generate all AI insights (title, summary, action items, notes) SYNCHRONOUSLY
      // This ensures the meeting is fully populated before the user sees it

      // Validation: Ensure transcript has meaningful content (minimum 20 words to avoid AI hallucinations on silence)
      const wordCount = finalTranscript?.trim().split(/\s+/).filter(w => w.length > 0).length || 0;
      const hasMinimumContent = finalTranscript &&
                                finalTranscript.trim().length > 100 &&
                                wordCount >= 20 &&
                                finalTranscript !== '(No speech detected in recording)';

      if (hasMinimumContent) {
        console.log(`🎯 Generating AI insights (word count: ${wordCount}, transcript length: ${finalTranscript.length} chars)...`);

        try {
          // Check if AI insights have already been generated (by database trigger)
          // to avoid duplicate calls
          let hasAIContent = false;

          try {
            const { data: meeting } = await supabase
              .from('meetings')
              .select('summary, action_items, notes')
              .eq('id', recording.id)
              .single();

            hasAIContent = !!(meeting && (meeting.summary || meeting.action_items || meeting.notes));
          } catch (checkErr) {
            console.warn('⚠️ Could not check if AI content exists, will attempt generation anyway:', checkErr);
            // Continue to generation attempt if check fails
            hasAIContent = false;
          }

          if (hasAIContent) {
            console.log('⏭️ AI insights already generated for this meeting (skipping duplicate call)');
          } else {
            // Only catch errors from generateAIInsights, not from the check
            try {
              await this.generateAIInsights(recording.id, finalTranscript, recording.template, participants);
              console.log('✅ AI insights generation completed successfully');
            } catch (genErr) {
              console.error('⚠️ AI insights generation failed:', genErr);
              // Don't throw - transcription is still successful even if insights fail
              // The meeting will be created with the fallback title
            }
          }
        } catch (err) {
          console.error('⚠️ Unexpected error in AI insights flow:', err);
          // Don't throw - transcription is still successful even if insights fail
        }
      } else {
        console.log(`⏩ Skipping AI insights generation - insufficient content (words: ${wordCount}, length: ${finalTranscript?.trim().length || 0} chars, min: 100 chars + 20 words)`);

        // Log this case for debugging - transcript exists but is too minimal
        if (finalTranscript && finalTranscript.trim().length > 0) {
          console.warn(`⚠️ Transcript exists but is too short for meaningful AI analysis:`, {
            length: finalTranscript.length,
            wordCount,
            content: finalTranscript.substring(0, 100)
          });
        }
      }

    } catch (error) {
      console.error('Failed to save transcription to database:', error);
      throw error;
    }
  }

  // Separate method for AI insights generation with proper error handling
  private async generateAIInsights(meetingId: string, transcription: string, template: string, participants?: string[]): Promise<void> {
    console.log(`🤖 Generating AI insights for meeting ${meetingId}...`);

    // Get the current session token to ensure authenticated call
    const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !currentSession?.access_token) {
      logger.error("No valid session for AI insights generation", sessionError);
      throw new Error("Authentication required - please refresh the page and try again");
    }

    // Call edge function with explicit Authorization header
    const { data, error } = await supabase.functions.invoke('generate-all-insights', {
      headers: {
        Authorization: `Bearer ${currentSession.access_token}`
      },
      body: {
        transcription,
        meetingId,
        template,
        participants: participants || [],
        generateTitle: true,
        mode: 'fast'
      }
    });

    if (error) {
      console.error('❌ AI insights generation error:', error);

      // FIX: Handle 404 gracefully - meeting might not exist in database yet (still offline)
      if (error.status === 404 || error.message?.includes('Meeting not found')) {
        console.warn('⚠️ Meeting not yet in database (still offline). Skipping insights generation.');
        console.warn('   The meeting will get insights once it\'s synced to the server via process-queue.');
        // Don't throw - this is expected for offline recordings
        return;
      }

      throw new Error(`AI insights failed: ${error.message || JSON.stringify(error)}`);
    }

    if (!data || !data.success) {
      console.error('❌ AI insights returned unsuccessful response:', data);

      // FIX: Also handle skipped responses gracefully
      if (data?.skipped) {
        console.log('⏭️ Insights already generated for this meeting');
        return;
      }

      throw new Error(data?.error || 'AI insights generation failed');
    }

    console.log('✅ AI insights generated successfully:', {
      title: data.title,
      hasSummary: !!data.summary,
      hasActionItems: !!data.action_items,
      hasAnalytics: !!(data.sentiment_analysis || data.key_topics)
    });
  }

  // Generate fallback title for initial meeting creation
  private generateFallbackTitle(): string {
    const now = new Date();
    return `Meeting ${now.toLocaleDateString()} ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  // FIX 3: Recover stuck pending_recordings (stuck-queue resilience)
  // Auto-pick up pending_recordings older than N minutes that weren't picked up by process-queue
  // This prevents them from being orphaned and causing "No Speech Detected" meetings
  async recoverStuckPendingRecordings(maxAgeMinutes: number = 30, userId?: string): Promise<void> {
    try {
      console.log(`🔧 Scanning for stuck pending_recordings older than ${maxAgeMinutes} minutes...`);

      // Build query
      let query = supabase
        .from('pending_recordings')
        .select('*')
        .eq('status', 'pending');

      // Filter by user if provided
      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data: stuckRecordings, error } = await query;

      if (error) {
        console.error('Error querying stuck recordings:', error);
        return;
      }

      if (!stuckRecordings || stuckRecordings.length === 0) {
        console.log('No stuck pending_recordings found');
        return;
      }

      const now = Date.now();
      const stuckItems = stuckRecordings.filter(item => {
        const createdAt = new Date(item.created_at).getTime();
        const ageMinutes = (now - createdAt) / 1000 / 60;
        return ageMinutes > maxAgeMinutes;
      });

      if (stuckItems.length === 0) {
        console.log('No stuck recordings older than threshold');
        return;
      }

      console.log(`Found ${stuckItems.length} stuck pending_recordings. Attempting recovery...`);

      for (const item of stuckItems) {
        try {
          console.log(`🔄 Retrying transcription for pending_recordings ${item.id} (age: ${Math.round((now - new Date(item.created_at).getTime()) / 1000 / 60)} min)`);

          // Trigger process-queue to handle this specific pending_recordings entry
          await supabase.functions.invoke('process-queue', {
            method: 'POST',
            body: { specificRecordingId: item.id }
          });

          toast.info(`Retrying stuck transcription: ${item.meeting_id.slice(0, 8)}...`);
        } catch (err) {
          console.error(`Failed to retry pending_recordings ${item.id}:`, err);
        }
      }

    } catch (error) {
      console.error('Error recovering stuck pending_recordings:', error);
      localLogger.captureError(error as Error, {
        component: 'adaptive_queue_processor',
        operation: 'recover_stuck_recordings'
      });
    }
  }

  // Utility method for blob conversion
  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        if (result && result.includes(',')) {
          resolve(result.split(',')[1]); // Remove data:audio/webm;base64, prefix
        } else {
          reject(new Error('Failed to convert audio to base64'));
        }
      };
      reader.onerror = () => reject(new Error('FileReader error'));
      reader.readAsDataURL(blob);
    });
  }

  // Get current processing statistics
  getProcessingStats(): QueueProcessingStats {
    return { ...this.processingStats };
  }

  // Get active processing tasks
  getActiveTasksCount(): number {
    return this.activeProcessingTasks.size;
  }

  // Emergency stop all processing
  emergencyStop(): void {
    console.warn('🛑 Emergency stop initiated - aborting all active tasks');

    for (const [taskId, controller] of this.activeProcessingTasks) {
      controller.abort();
      this.activeProcessingTasks.delete(taskId);
    }

    this.isProcessing = false;
    toast.warning('Processing stopped - all active tasks aborted');
  }

  // Filter out recordings that are already locked by other tabs
  // This prevents retry storms by skipping recordings currently being processed
  private async filterLockedRecordings(
    recordings: PendingRecording[]
  ): Promise<{ unlocked: PendingRecording[]; locked: PendingRecording[] }> {
    const unlocked: PendingRecording[] = [];
    const locked: PendingRecording[] = [];

    for (const recording of recordings) {
      try {
        const lockStatus = await distributedLockManager.isLocked(recording.id);

        if (lockStatus) {
          // Recording is already locked by another tab
          console.log(`🔒 Recording ${recording.id} is locked by instance: ${lockStatus.processing_instance_id}`);
          locked.push(recording);
        } else {
          // Recording is not locked, safe to process
          unlocked.push(recording);
        }
      } catch (error) {
        // If we can't check lock status, treat as unlocked (safe default)
        console.warn(`⚠️ Failed to check lock for ${recording.id}, assuming unlocked:`, error);
        unlocked.push(recording);
      }
    }

    return { unlocked, locked };
  }

  // Configure concurrent processing limits
  setMaxConcurrentProcessing(limit: number): void {
    this.maxConcurrentProcessing = Math.max(1, Math.min(limit, 5)); // Limit between 1-5
    console.log(`Max concurrent processing set to: ${this.maxConcurrentProcessing}`);
  }
}

export const adaptiveQueueProcessor = new AdaptiveQueueProcessor();